import { addDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import {
  offlineStorage,
  PendingOperation,
  PendingClient,
  PendingReceipt,
} from './offlineStorage';
import { createReceiptWithUniqueNumber } from './receiptNumberReservation';
import { normalizePolishText, createSearchableText } from './textUtils';
import { checkWriteRateLimit } from './rateLimitService';
import toast from 'react-hot-toast';
import { idMappingService } from './idMappingService';
import { cacheUpdateService } from './cacheUpdateService';
import { logger } from './logger';
import { isErrorWithMessage } from '../types/common';
import {
  isTransientSyncError,
  syncErrorDisposition,
} from './syncErrorClassification';
import { clientDocument, clientsCollection } from '../lib/paths';

// A permanently-failing operation is quarantined after this many attempts.
// Transient failures (offline, rules propagation, contention) do not count.
const MAX_SYNC_ATTEMPTS = 3;

export interface SyncResult {
  success: boolean;
  syncedOperations: number;
  failedOperations: number;
  /** Operations moved to quarantine this run — never deleted */
  quarantinedOperations: number;
  errors: string[];
  cacheUpdates?: {
    updatedClients: number;
    updatedReceipts: number;
    removedTempEntries: number;
  };
}

class SyncService {
  private isSyncing = false;
  private syncCallbacks: ((result: SyncResult) => void)[] = [];

  // Register callback for sync completion
  onSyncComplete(callback: (result: SyncResult) => void): void {
    this.syncCallbacks.push(callback);
  }

  // Remove sync callback
  removeSyncCallback(callback: (result: SyncResult) => void): void {
    this.syncCallbacks = this.syncCallbacks.filter(cb => cb !== callback);
  }

  private notifySyncComplete(result: SyncResult): void {
    this.syncCallbacks.forEach(callback => callback(result));
  }

  async syncPendingOperations(userUID: string): Promise<SyncResult> {
    if (this.isSyncing) {
      return {
        success: false,
        syncedOperations: 0,
        failedOperations: 0,
        quarantinedOperations: 0,
        errors: ['Sync already in progress'],
      };
    }

    // Claim the slot synchronously, before the first await. Reading the flag
    // and setting it on opposite sides of an await let two triggers arriving
    // in the same tick — the online event and the periodic check, say — both
    // pass the guard and replay the whole queue, writing every pending
    // operation twice.
    this.isSyncing = true;

    const result: SyncResult = {
      success: true,
      syncedOperations: 0,
      failedOperations: 0,
      quarantinedOperations: 0,
      errors: [],
    };

    // Everything after setting isSyncing runs inside try/finally — an
    // unexpected throw must never leave the flag stuck, or the app would
    // serve stale cache forever while appearing online
    try {
      // Check rate limits for sync operation
      const syncLimit = await checkWriteRateLimit();
      if (!syncLimit.allowed) {
        const message =
          syncLimit.message ||
          'Zbyt wiele operacji synchronizacji. Spróbuj ponownie później.';
        toast.error(message);
        return {
          success: false,
          syncedOperations: 0,
          failedOperations: 1,
          quarantinedOperations: 0,
          errors: [message],
        };
      }

      // Clear previous mappings and prepare for new sync
      idMappingService.clear();

      const pendingOperations = await offlineStorage.getPendingOperations();

      if (pendingOperations.length === 0) {
        return result;
      }

      // Sort operations by timestamp to maintain order
      const sortedOperations = pendingOperations.sort(
        (a, b) => a.timestamp - b.timestamp
      );

      // Phase 1: Build dependency graph before syncing
      this.buildDependencyGraph(sortedOperations);

      // Phase 2: Sync operations
      for (const operation of sortedOperations) {
        try {
          await this.syncOperation(operation, userUID);
          await offlineStorage.removePendingOperation(operation.id);
          result.syncedOperations++;
        } catch (error) {
          logger.error(
            `Failed to sync operation: ${operation.type} (${operation.id})`,
            isErrorWithMessage(error) ? error : undefined,
            {
              component: 'SyncService',
              operation: 'syncPendingOperations',
              extra: {
                operationType: operation.type,
                operationId: operation.id,
              },
            }
          );
          result.failedOperations++;
          const message =
            error instanceof Error ? error.message : 'Unknown error';
          result.errors.push(`${operation.type}: ${message}`);

          // A transient failure means this exact write could still succeed
          // later — it must not consume the retry budget, or a spell of bad
          // connectivity would be enough to push the operation out of the
          // queue. Only permanent failures count toward quarantine.
          //
          // A denial by security rules is transient only for a while: stamp
          // the first one so the window can close, and let the operation start
          // spending its budget after that. Without the stamp it would retry
          // against a rule that keeps saying no, forever, and never surface.
          const deniedNow = syncErrorDisposition(error) === 'bounded';
          const firstDeniedAt =
            deniedNow && operation.firstDeniedAt === undefined
              ? Date.now()
              : operation.firstDeniedAt;

          if (deniedNow && operation.firstDeniedAt === undefined) {
            await offlineStorage.updatePendingOperation(operation.id, {
              firstDeniedAt,
            });
          }

          if (isTransientSyncError(error, firstDeniedAt)) {
            continue;
          }

          const retryCount = operation.retryCount + 1;
          if (retryCount >= MAX_SYNC_ATTEMPTS) {
            // Quarantined, never discarded: the user's offline work stays
            // recoverable from the Dane Offline page
            await offlineStorage.quarantinePendingOperation(
              operation.id,
              message
            );
            result.quarantinedOperations++;
            result.errors.push(
              `${operation.type}: Przeniesiono do wymagających uwagi po ${MAX_SYNC_ATTEMPTS} próbach`
            );
          } else {
            await offlineStorage.updatePendingOperation(operation.id, {
              retryCount,
            });
          }
        }
      }

      if (result.failedOperations > 0) {
        result.success = false;
      }

      // Phase 3: Apply cache updates using the decoupled services.
      // Runs for ANY successfully synced operation, even when later ones
      // failed — otherwise the succeeded ops' temp IDs are never remapped
      // and duplicates/orphans appear on the next sync
      if (result.syncedOperations > 0) {
        const cacheUpdateResult = await this.applyCacheUpdates();
        result.cacheUpdates = {
          updatedClients: cacheUpdateResult.updatedClients,
          updatedReceipts: cacheUpdateResult.updatedReceipts,
          removedTempEntries: cacheUpdateResult.removedTempEntries,
        };

        if (!cacheUpdateResult.success) {
          result.errors.push(...cacheUpdateResult.errors);
        }

        // Verify cache consistency
        const consistencyCheck =
          await cacheUpdateService.verifyCacheConsistency();
        if (!consistencyCheck.isConsistent) {
          logger.warn('Cache consistency issues found', undefined, {
            component: 'SyncService',
            operation: 'syncPendingOperations',
            extra: {
              issues: consistencyCheck.issues,
              autoFixed: consistencyCheck.fixed,
            },
          });
          if (consistencyCheck.fixed.length > 0) {
            logger.debug('Auto-fixed cache issues', undefined, {
              component: 'SyncService',
              operation: 'syncPendingOperations',
              extra: { fixed: consistencyCheck.fixed },
            });
          }
        }
      }
    } finally {
      // Clean up mapping service
      idMappingService.clear();
      this.isSyncing = false;
    }

    this.notifySyncComplete(result);

    // Show user-friendly toast
    if (result.success && result.syncedOperations > 0) {
      toast.success(
        `Zsynchronizowano ${result.syncedOperations} operacji offline`
      );
    } else if (result.failedOperations > 0) {
      toast.error(
        `Nie udało się zsynchronizować ${result.failedOperations} operacji`
      );
    }

    // Quarantine is a separate, louder message: the data is safe but it will
    // not sync on its own again and needs the user to look at it
    if (result.quarantinedOperations > 0) {
      toast.error(
        `${result.quarantinedOperations} operacji wymaga uwagi — dane są zachowane. ` +
          'Otwórz Dane Offline, aby ponowić lub usunąć.',
        { duration: 10000 }
      );
    }

    return result;
  }

  /**
   * Build dependency graph to track receipt-client relationships
   */
  private buildDependencyGraph(operations: PendingOperation[]): void {
    for (const operation of operations) {
      if (operation.type === 'CREATE_RECEIPT') {
        const receiptData = operation.data as PendingReceipt;
        if (receiptData.tempId && receiptData.clientId) {
          idMappingService.addDependency(
            receiptData.tempId,
            receiptData.clientId
          );
        }
      }
    }
  }

  /**
   * Apply cache updates using the decoupled services
   */
  private async applyCacheUpdates() {
    // Generate batch updates from ID mappings
    const updates = idMappingService.generateCacheUpdates();

    // Apply the updates
    const updateResult =
      await cacheUpdateService.applyIdMappingUpdates(updates);

    // Clean up temporary entries
    const cleanupResult = await cacheUpdateService.cleanupTempEntries();

    // Combine results
    return {
      success: updateResult.success && cleanupResult.success,
      updatedClients: updateResult.updatedClients,
      updatedReceipts: updateResult.updatedReceipts,
      removedTempEntries: cleanupResult.removedTempEntries,
      errors: [...updateResult.errors, ...cleanupResult.errors],
    };
  }

  private async syncOperation(
    operation: PendingOperation,
    userUID: string
  ): Promise<void> {
    switch (operation.type) {
      case 'CREATE_CLIENT':
        await this.syncCreateClient(operation.data as PendingClient, userUID);
        break;
      case 'CREATE_RECEIPT':
        await this.syncCreateReceipt(operation.data as PendingReceipt, userUID);
        break;
      case 'UPDATE_CLIENT':
        await this.syncUpdateClient(operation.data, userUID);
        break;
      case 'DELETE_CLIENT':
        // For DELETE_CLIENT operations, the data should contain clientId
        const deleteData = operation.data as any; // Type assertion for delete operation
        await this.syncDeleteClient(deleteData.clientId, userUID);
        break;
      default:
        throw new Error(`Unknown operation type: ${operation.type}`);
    }
  }

  private async syncCreateClient(
    clientData: PendingClient,
    userUID: string
  ): Promise<void> {
    // Remove temp fields and resolve any temp IDs
    const { tempId, ...cleanClientData } = clientData;
    const resolvedData = idMappingService.resolveIds(cleanClientData);

    const clientToCreate = {
      ...resolvedData,
    };

    // Add server-side fields
    const addressParts = [];
    if (clientToCreate.address.trim())
      addressParts.push(clientToCreate.address.trim());
    if (clientToCreate.postalCode?.trim() || clientToCreate.city?.trim()) {
      const locationPart = [
        clientToCreate.postalCode?.trim(),
        clientToCreate.city?.trim(),
      ]
        .filter(Boolean)
        .join(' ');
      if (locationPart) addressParts.push(locationPart);
    }
    const fullAddress = addressParts.join(', ');

    const finalClientData = {
      ...clientToCreate,
      fullAddress,
      name_lowercase: clientToCreate.name.toLowerCase(),
      name_normalized: normalizePolishText(clientToCreate.name),
      address_normalized: normalizePolishText(clientToCreate.address),
      documentNumber_normalized: normalizePolishText(
        clientToCreate.documentNumber
      ),
      postalCode_normalized: normalizePolishText(
        clientToCreate.postalCode || ''
      ),
      city_normalized: normalizePolishText(clientToCreate.city || ''),
      fullAddress_normalized: normalizePolishText(fullAddress),
      searchableText: createSearchableText([
        clientToCreate.name,
        clientToCreate.address,
        clientToCreate.documentNumber,
        clientToCreate.postalCode || '',
        clientToCreate.city || '',
        fullAddress,
      ]),
    };

    // Create in Firebase
    const docRef = await addDoc(clientsCollection(userUID), finalClientData);

    // Register the mapping for later use
    idMappingService.addMapping(tempId, docRef.id, 'client');

    // Replace temp entity in cache with real entity
    await cacheUpdateService.replaceEntity(
      tempId,
      docRef.id,
      { ...finalClientData, id: docRef.id },
      'client'
    );
  }

  private async syncCreateReceipt(
    receiptData: PendingReceipt,
    userUID: string
  ): Promise<void> {
    // Remove temp fields and resolve any temp IDs
    const { tempId, ...cleanReceiptData } = receiptData;
    const resolvedData = idMappingService.resolveIds(cleanReceiptData);

    const receiptToCreate = {
      ...resolvedData,
      date: new Date(resolvedData.date), // Ensure it's a proper Date object
    };

    // Create in Firebase, reserving the receipt number atomically. The
    // offline-generated number is provisional: if another device claimed it
    // first, the transaction bumps to the next free number for that day
    const { id: newId, number: finalNumber } =
      await createReceiptWithUniqueNumber(userUID, receiptToCreate, {
        fallbackDate: receiptToCreate.date,
      });

    if (finalNumber !== receiptToCreate.number) {
      logger.warn(
        `Offline receipt number was taken, renumbered on sync: ${receiptToCreate.number} -> ${finalNumber}`,
        undefined,
        {
          component: 'SyncService',
          operation: 'syncCreateReceipt',
          extra: { requestedNumber: receiptToCreate.number, finalNumber },
        }
      );
    }

    // Register the mapping
    idMappingService.addMapping(tempId, newId, 'receipt');

    // Replace temp entity in cache with real entity
    await cacheUpdateService.replaceEntity(
      tempId,
      newId,
      { ...receiptToCreate, number: finalNumber, id: newId },
      'receipt'
    );
  }

  private async syncUpdateClient(
    clientData: any,
    userUID: string
  ): Promise<void> {
    const { id, ...updateData } = clientData;
    const resolvedData = idMappingService.resolveIds(updateData);

    await updateDoc(clientDocument(userUID, id), {
      ...resolvedData,
    });
  }

  private async syncDeleteClient(
    clientId: string,
    userUID: string
  ): Promise<void> {
    await deleteDoc(clientDocument(userUID, clientId));

    // Remove from cache using the decoupled service
    await cacheUpdateService.removeEntity(clientId, 'client');
  }

  // Get sync status
  getIsSyncing(): boolean {
    return this.isSyncing;
  }

  // Check if there are pending operations
  async hasPendingOperations(): Promise<boolean> {
    const operations = await offlineStorage.getPendingOperations();
    return operations.length > 0;
  }

  // Get pending operations count
  async getPendingOperationsCount(): Promise<number> {
    const operations = await offlineStorage.getPendingOperations();
    return operations.length;
  }

  // Quarantined operations awaiting a decision from the user
  async getFailedOperationsCount(): Promise<number> {
    const operations = await offlineStorage.getFailedOperations();
    return operations.length;
  }

  /**
   * Re-queue every quarantined operation and sync immediately. Used by the
   * retry button on the Dane Offline page.
   */
  async retryFailedOperations(userUID: string): Promise<SyncResult> {
    const restored = await offlineStorage.restoreAllFailedOperations();
    if (restored === 0) {
      return {
        success: true,
        syncedOperations: 0,
        failedOperations: 0,
        quarantinedOperations: 0,
        errors: [],
      };
    }
    return this.syncPendingOperations(userUID);
  }

  // Debugging and monitoring methods
  async getSyncStatistics() {
    const pendingOpsCount = await this.getPendingOperationsCount();
    const mappingStats = idMappingService.getStats();

    return {
      isSyncing: this.isSyncing,
      pendingOperations: pendingOpsCount,
      idMappings: mappingStats,
      timestamp: Date.now(),
    };
  }

  async validateSync(): Promise<{ isValid: boolean; issues: string[] }> {
    const mappingValidation = idMappingService.validateMappings();
    const cacheConsistency = await cacheUpdateService.verifyCacheConsistency();

    return {
      isValid: mappingValidation.isValid && cacheConsistency.isConsistent,
      issues: [...mappingValidation.errors, ...cacheConsistency.issues],
    };
  }
}

// Export singleton instance
const syncService = new SyncService();
export { syncService };
export default syncService;
